import React, { useCallback, useEffect, useState } from "react";
import { addToast, Button } from "@heroui/react";
import StakePopup from "./stake-popup";
import {
  NftListForStakingApi,
  NftStakingApi,
  writeContractApi
} from "../../../api/methods-marketplace";
import StakingCardLoader from "./staking-card/staking-card-loader";
import StakingCard from "./staking-card";
import nftABI from "../../../abis/nft-abi.json";
import stakingABI from "../../../abis/nft-staking-abi.json";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useWallet } from "../../../context/WalletContext";
import {
  readContract,
  waitForTransactionReceipt,
  writeContract
} from "@wagmi/core";
import { rainbowKitConfig } from "../../../utils/config";
import { getUser, isUserLoggedIn } from "../../../redux/reducer/user_reducer";
import { encodeFunctionData } from "viem";

const NftStakingTab = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isStaking, setIsStaking] = useState(null);
  const [isOpenStakePopup, setIsOpenStakePopup] = useState(false);
  const [data, setData] = useState([]);
  const {
    isConnected,
    address,
    hasAddressMismatch,
    isCustodialWallet,
    publicClient
  } = useWallet();
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const isLoggedIn = useSelector(isUserLoggedIn);
  const NFT_CONTRACT_ADDRESS = process.env.REACT_APP_NFT_CONTRACT_ADDRESS;
  const STAKING_CONTRACT_ADDRESS =
    process.env.REACT_APP_STAKING_CONTRACT_ADDRESS;

  const fetchNftList = useCallback(async () => {
    setIsLoading(true);

    if (!user?.address) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await NftListForStakingApi({
        method: "no",
        address: user.address
      });

      setData(response?.data?.data || []);
    } catch (error) {
      console.error("❌ Failed to fetch nft list:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.address]);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsLoading(false);
      return;
    }

    if (!user?.address) {
      setIsLoading(false);
      return;
    }

    fetchNftList();
  }, [isLoggedIn, address, user?.address, fetchNftList]);

  const handleNftStaking = async (tokenId, nftAddess) => {
    if (!isCustodialWallet) {
      if (!isConnected) {
        addToast({
          title: "Not Connected",
          description: "Please connect your wallet first.",
          color: "danger",
          variant: "bordered"
        });
        return;
      }
      if ((!user?.address && address) || hasAddressMismatch) {
        addToast({
          title: hasAddressMismatch ? "Address Mismatch" : "Profile Incomplete",
          description: hasAddressMismatch
            ? "The connected wallet address does not match your registered address. Please reconnect with the correct one."
            : "Wallet address belongs to another account. Please reconnect with the correct one.",
          color: "warning",
          variant: "bordered"
        });
        return;
      }
      try {
        setIsStaking(tokenId);

        // 2️⃣ Read owner of the token
        const ownerOf = await readContract(rainbowKitConfig, {
          abi: nftABI,
          address: NFT_CONTRACT_ADDRESS,
          functionName: "ownerOf",
          args: [tokenId]
        });

        if (ownerOf.toLowerCase() !== address.toLowerCase()) {
          addToast({
            title: "Not Owner",
            description: "You do not own this NFT.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        const approvedAddress = await readContract(rainbowKitConfig, {
          abi: nftABI,
          address: NFT_CONTRACT_ADDRESS,
          functionName: "getApproved",
          args: [tokenId]
        });

        const isApproved =
          approvedAddress.toLowerCase() ===
          STAKING_CONTRACT_ADDRESS.toLowerCase();
        if (!isApproved) {
          const approvalTx = await writeContract(rainbowKitConfig, {
            account: address,
            abi: nftABI,
            address: NFT_CONTRACT_ADDRESS,
            functionName: "approve",
            args: [STAKING_CONTRACT_ADDRESS, tokenId]
          });

          const approvalReceipt = await waitForTransactionReceipt(
            rainbowKitConfig,
            {
              hash: approvalTx,
              confirmations: 1
            }
          );

          if (approvalReceipt.status !== "success") {
            addToast({
              title: "approval transaction failed",
              color: "danger",
              variant: "bordered"
            });
            return;
          }
        }

        const stakeTx = await writeContract(rainbowKitConfig, {
          account: address,
          abi: stakingABI,
          address: STAKING_CONTRACT_ADDRESS,
          functionName: "stake",
          args: [[tokenId]]
        });

        await waitForTransactionReceipt(rainbowKitConfig, { hash: stakeTx });

        const data = {
          method: "stake",
          collectionId: nftAddess,
          hash: stakeTx
        };
        updateStakingApi(data);
      } catch (error) {
        console.error("🚀 ~ handleNftStaking ~ error:", error);
      } finally {
        setIsStaking(null);
      }
    } else if (isCustodialWallet) {
      try {
        setIsStaking(tokenId);

        const owner = await publicClient.readContract({
          address: NFT_CONTRACT_ADDRESS,
          abi: nftABI,
          functionName: "ownerOf",
          args: [tokenId]
        });
        console.log("Owner:", owner);

        if (owner.toLowerCase() !== address.toLowerCase()) {
          addToast({
            title: "Not Owner",
            description: "You do not own this NFT.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        const approvedAddress = await publicClient.readContract({
          address: NFT_CONTRACT_ADDRESS,
          abi: nftABI,
          functionName: "getApproved",
          args: [tokenId]
        });

        const isApproved =
          approvedAddress.toLowerCase() ===
          STAKING_CONTRACT_ADDRESS.toLowerCase();

        if (!isApproved) {
          const approveCallData = encodeFunctionData({
            abi: nftABI,
            functionName: "approve",
            args: [STAKING_CONTRACT_ADDRESS, tokenId]
          });

          const approvalTx = await writeContractApi({
            address: NFT_CONTRACT_ADDRESS,
            encoded_data: approveCallData
          });

          if (!approvalTx?.data?.tx_hash) {
            throw new Error("Approval transaction hash not received");
          }

          const approvalTxHash = approvalTx.data.tx_hash;
          console.log("Approval tx hash:", approvalTxHash);

          // Wait for approval transaction
          const approvalReceipt = await publicClient.waitForTransactionReceipt({
            hash: approvalTxHash
          });

          console.log("Approval receipt:", approvalReceipt);

          if (approvalReceipt.status !== "success") {
            addToast({
              title: "Approval Failed",
              description: "Approval transaction failed",
              color: "danger",
              variant: "bordered"
            });
            return;
          }
        }

        // Step 2: Stake the NFT
        const stakeCallData = encodeFunctionData({
          abi: stakingABI,
          functionName: "stake",
          args: [[tokenId]]
        });

        const stakeTx = await writeContractApi({
          address: STAKING_CONTRACT_ADDRESS,
          encoded_data: stakeCallData
        });

        if (!stakeTx?.data?.tx_hash) {
          throw new Error("Staking transaction hash not received");
        }

        const stakeTxHash = stakeTx.data.tx_hash;
        console.log("Stake tx hash:", stakeTxHash);

        // Wait for staking transaction
        const stakeReceipt = await publicClient.waitForTransactionReceipt({
          hash: stakeTxHash
        });

        console.log("Stake receipt:", stakeReceipt);

        if (stakeReceipt?.status !== "success") {
          addToast({
            title: "Staking Failed",
            description: "Oops..! Failed to stake NFT",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        // Update backend
        const data = {
          method: "stake",
          collectionId: nftAddess,
          hash: stakeTxHash
        };
        await updateStakingApi(data);

        addToast({
          title: "Staking Successful!",
          description: "Your NFT has been staked successfully.",
          color: "success",
          variant: "bordered"
        });
      } catch (err) {
        console.error("Custodial staking error:", err);

        addToast({
          title: "Staking Failed",
          description:
            err?.response?.data?.message ||
            err?.shortMessage ||
            err?.message ||
            "Failed to stake NFT",
          color: "danger",
          variant: "bordered"
        });
      } finally {
        setIsStaking(null);
      }
    }
  };

  const updateStakingApi = async (data) => {
    try {
      const response = await NftStakingApi(data);
      if (response) {
        addToast({
          title: "NFT Staked Successfully",
          description: "Your NFT has been staked successfully.",
          color: "success",
          variant: "bordered"
        });
        setIsStaking(null);
        fetchNftList();
        setIsOpenStakePopup(true);
      }
    } catch (error) {
      const message =
        error?.reason ||
        error?.data?.message ||
        (error?.message?.includes("ERC721")
          ? "You are not the token owner or approved."
          : "Transaction failed.");

      console.error("❌ Transaction Error:", error);

      addToast({
        title: "Transaction Failed",
        description: message,
        color: "danger",
        variant: "bordered"
      });
    }
  };

  const renderTabContent = () => {
    if (!isLoggedIn) {
      return (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="w-20 h-20 mb-6 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            Login Required
          </h2>
          <p className="text-gray-600 text-center max-w-md mb-8">
            Please log in to access NFT staking features and manage your digital
            assets.
          </p>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <Button
              className="bg-yellowbuy border border-black rounded-lg font-medium px-6 py-2"
              onPress={() => navigate("/login")}
            >
              Login to Your Account
            </Button>
            <Button
              className="bg-white border border-gray-300 rounded-lg font-medium px-6 py-2"
              onPress={() => navigate("/sign-up")}
            >
              Create Account
            </Button>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full">
          {Array.from({ length: 12 }, (_, index) => (
            <StakingCardLoader key={`loader-${index}`} />
          ))}
        </div>
      );
    }

    if (data?.length > 0) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full">
          {data.map((collection, index) => (
            <StakingCard
              key={`nft-${collection.id || index}`}
              collection={collection}
              onClick={handleNftStaking}
              data={collection}
              isStaking={isStaking}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"
            />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          No NFTs available for staking
        </h3>
        <p className="text-gray-600 text-center max-w-md mb-6">
          You don’t have any eligible NFTs to stake right now. Mint or buy NFTs
          to get started!
        </p>
        <div className="flex space-x-4">
          <Button
            className="bg-yellowbuy border border-black rounded-lg"
            onPress={() => navigate("/marketplace")}
          >
            Browse Marketplace
          </Button>
        </div>
      </div>
    );
  };

  const handleStakingPopup = () => {
    setIsOpenStakePopup(!isOpenStakePopup);
  };

  const handleCloseStakingPopup = () => {
    setIsOpenStakePopup(false);
  };

  return (
    <>
      {renderTabContent()}
      {isOpenStakePopup && (
        <StakePopup
          isOpen={isOpenStakePopup}
          onOpenChange={handleStakingPopup}
          onClosePopup={handleCloseStakingPopup}
        />
      )}
    </>
  );
};

export default NftStakingTab;
